import { useCallback, useEffect, useState } from 'react';
import { useActiveEstate } from '@/hooks/useActiveEstate';
import { clearOAuthError, readOAuthError } from '@/lib/authError';
import { UploadIndicator } from './PhotoUploads';
import { SignInForm } from './SignInForm';
import { Drawer, Muted, RosaIcon } from './ui';

/**
 * App header: the active project's name (RosaHouse before there is one), the Signup-to-Sync
 * affordance, and the project menu button. Anonymous users work fully offline-first in a
 * local store; signing up adopts it (`useAnonClaim`).
 */
export function SyncBanner({ author, onMenu }: { author: string | null; onMenu: () => void }) {
  const { activeEstate } = useActiveEstate();
  // A refused Google sign-up lands back here with `?error=`; reopen the form to say why.
  const [oauthError] = useState(readOAuthError);
  const [showSignup, setShowSignup] = useState(oauthError !== null);
  const closeSignup = useCallback(() => setShowSignup(false), []);
  useEffect(clearOAuthError, []);

  return (
    <section aria-label="Sync status" className="border-b border-amber-200 bg-amber-50">
      <div className="mx-auto flex max-w-md items-center justify-between gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={onMenu}
          className="flex min-w-0 items-center gap-2 rounded-lg text-base font-bold text-amber-900"
        >
          <RosaIcon />
          <span className="truncate">{activeEstate?.name || 'RosaHouse'}</span>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <UploadIndicator />
          {!author && (
            <button
              type="button"
              onClick={() => setShowSignup(true)}
              className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
            >
              Sign up to sync
            </button>
          )}
          <button
            type="button"
            aria-label="Project menu"
            title="Project menu"
            onClick={onMenu}
            className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg px-2 text-lg font-bold text-amber-900 hover:bg-amber-100"
          >
            <span aria-hidden="true">☰</span>
          </button>
        </div>
      </div>
      {showSignup && !author && (
        <Drawer title="Sign up to sync" onClose={closeSignup}>
          <div className="space-y-3">
            <Muted>You can keep capturing without an account. It all stays on this device.</Muted>
            <SignInForm initialError={oauthError} />
          </div>
        </Drawer>
      )}
    </section>
  );
}
